/**
 * #511 — thin wrapper over Supabase Auth's native TOTP MFA API.
 *
 * No custom crypto/storage here — enrollment, challenge, and verification
 * are all handled by Supabase Auth (GoTrue) itself; this module only
 * shapes the calls/responses for this app's UI.
 *
 * Backup/recovery codes are explicitly NOT implemented — Supabase Auth's
 * MFA API has no backup-code primitive, and building one is a separate
 * subsystem (see 20260821020000_aal2_admin_command_enforcement.sql's
 * header for the interim mitigation: admin_unenroll_mfa_factor()).
 */
import { supabase } from '@/integrations/supabase/client';

export interface MfaFactor {
  id: string;
  factorType: string;
  status: 'verified' | 'unverified';
  friendlyName?: string | null;
  createdAt: string;
}

/**
 * Every TOTP factor on the account, VERIFIED OR NOT.
 *
 * It has to read `data.all`, not `data.totp`. auth-js fills the per-type
 * buckets only with verified factors:
 *
 *     for (const factor of user?.factors ?? []) {
 *       data.all.push(factor);
 *       if (factor.status === 'verified') { data[factor.factor_type].push(factor) }
 *     }
 *
 * so `data.totp` can never contain an unverified one. Reading it made
 * beginTotpEnrollment's stale-factor cleanup unreachable -- its
 * `status !== 'verified'` test was false by construction -- and the lockout
 * that cleanup exists to prevent stayed live under a comment saying it was
 * fixed (2026-09-09).
 *
 * Every caller filters for `verified` itself where it wants only those, so
 * widening this is safe: StepUpChallengeDialog, MfaEnrollment and
 * hasVerifiedTotpFactor all do `.find`/`.some` on the status.
 */
export async function listMfaFactors(): Promise<MfaFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return (data?.all ?? []).filter((f) => f.factor_type === 'totp').map((f) => ({
    id: f.id,
    factorType: f.factor_type,
    status: f.status,
    friendlyName: f.friendly_name,
    createdAt: f.created_at,
  }));
}

export async function hasVerifiedTotpFactor(): Promise<boolean> {
  const factors = await listMfaFactors();
  return factors.some((f) => f.status === 'verified');
}

/**
 * Turns whatever GoTrue returns for `totp.qr_code` into something an <img src>
 * will actually render.
 *
 * The previous form was `data:image/svg+xml;utf-8,...`, which is malformed: in
 * a data URI everything before the comma after the media type must be a
 * `parameter=value` pair, so the charset has to be written `charset=utf-8`. A
 * bare `utf-8` is an unrecognised parameter. Chrome tolerates it; **iOS Safari
 * does not**, and renders nothing — which is exactly how this surfaced, as a
 * blank QR on an iPhone while the enrollment itself had already succeeded.
 *
 * That blankness was the root cause of the lockout, not a cosmetic issue:
 * enrollment creates an unverified factor the moment the QR is requested, so a
 * QR nobody can scan means a factor nobody can verify, left behind on every
 * attempt.
 *
 * Also tolerates the SDK handing back a ready-made data URI, so a future
 * supabase-js change in either direction keeps working.
 */
function toSvgDataUri(qrCode: string): string {
  const raw = (qrCode ?? '').trim();
  if (raw.startsWith('data:')) return raw;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(raw)}`;
}

/** Starts enrollment; returns a ready-to-render <img src> data URI for the
 * QR code and the plain-text secret (shown as a manual-entry fallback)
 * plus the factor id needed to complete verification. */
export async function beginTotpEnrollment(friendlyName?: string): Promise<{
  factorId: string;
  qrCodeDataUri: string;
  secret: string;
}> {
  // An abandoned attempt leaves an UNVERIFIED factor behind, and GoTrue refuses
  // a second enrollment reusing the same friendly name. So a second tap of
  // "Enable" failed with `A factor with the friendly name "..." already exists`
  // while the card still read "Not enrolled" — because that badge counts only
  // VERIFIED factors. Anyone who opened the QR and did not finish was then
  // permanently unable to enrol, which for staff past the rollout deadline
  // means permanently locked out of every AAL2-gated action: refunds, payouts,
  // KYC approval, suspensions.
  //
  // Clearing stale unverified factors first makes enrollment retryable. ONLY
  // unverified ones: a verified factor is the user's real second factor and
  // must never be discarded by a retry.
  // Every unverified factor, not just ones sharing this friendly name. An
  // unverified factor is by definition not anyone's second factor -- it is the
  // residue of an abandoned attempt -- and matching on the name coupled the
  // cleanup to a string that is now per-account, so a rename would have
  // silently reintroduced the lockout.
  for (const stale of await listMfaFactors()) {
    if (stale.status !== 'verified') {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: stale.id });
      if (error) throw error;
    }
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName,
  });
  if (error) throw error;
  return {
    factorId: data.id,
    qrCodeDataUri: toSvgDataUri(data.totp.qr_code),
    secret: data.totp.secret,
  };
}

/** Completes enrollment (or a step-up challenge) by verifying a 6-digit
 * TOTP code against the given factor. */
export async function verifyTotpCode(factorId: string, code: string): Promise<void> {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
  if (challengeError) throw challengeError;

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  if (verifyError) throw verifyError;
}

export async function unenrollTotpFactor(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw error;
}

/** The Supabase `session_id` claim of an access token. It stays the same when
 * the token refreshes or steps up to AAL2, and changes on a new sign-in. */
export function sessionIdFromAccessToken(accessToken: string): string | null {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const sessionId = (JSON.parse(json) as { session_id?: unknown }).session_id;
    return typeof sessionId === 'string' && sessionId ? sessionId : null;
  } catch {
    return null;
  }
}

/** Current vs. next required AAL for this session — `nextLevel !==
 * currentLevel` means a step-up challenge is needed before an AAL2-gated
 * action will succeed. */
export async function getAssuranceLevel(): Promise<{
  currentLevel: string | null;
  nextLevel: string | null;
}> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw error;
  return { currentLevel: data.currentLevel, nextLevel: data.nextLevel };
}

/** The Postgres error code enforce_aal2_or_log() raises — both for "step up
 * needed" (enrolled, AAL1) and "enrollment now required" (past the rollout
 * deadline, unenrolled). The UI treats both the same way: prompt for a
 * step-up/enrollment flow, then retry. */
export const AAL2_REQUIRED_ERROR_CODE = '28000';

export function isAal2RequiredError(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return code === AAL2_REQUIRED_ERROR_CODE;
}

/**
 * Where the signed-in person enrols an authenticator, for the surface they
 * are on. The step-up dialog sends an un-enrolled person here instead of
 * asking for a code they cannot have: an admin on 2026-10-28 opened the
 * enrollment QR on a phone, never pressed Confirm (the factor stayed
 * `unverified`), then typed a code into the step-up dialog on a laptop and
 * was told to "contact support" — as the only super_admin.
 */
export function mfaEnrollmentPath(pathname: string = window.location.pathname): string {
  if (pathname.startsWith('/admin')) return '/admin/account-security';
  if (pathname.startsWith('/dashboard/helper') || pathname.startsWith('/helper-account')) return '/helper-account?tab=security';
  return '/client-account?tab=security';
}
